// The public half of the site: the frame every route renders inside, and the
// marketing, docs and legal routes that are pre-rendered to static HTML.
//
// SiteApp.tsx composes these with the signed-in, play and editor routes for
// the browser; src/entry-server.tsx composes them on their own for the build's
// pre-render. Keeping the public routes here, away from SiteApp's imports, is
// what keeps the engine (Three.js, WebGL, the editor) off the server render
// path: nothing this module imports reaches src/engine at module scope.
import { useEffect, useRef, type ComponentType, type ReactNode } from 'react';
import { Route, useParams } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { NotFoundScreen } from './components/ui-shared';
import { ErrorBoundary } from './design/ErrorBoundary';
import { NavigateBridge, navigate, useRoute } from './design/router';
import { ToastProvider } from './design/toast';
import { DOC_PAGES } from './docs/docsContent';
import { ScreenDocsHome, ScreenDocsPage } from './docs/screens-docs';
import { ScreenBlog, ScreenBlogPost, ScreenChangelog } from './marketing/screens-blog';
import { ScreenAbout, ScreenAttributions, ScreenContact, ScreenLegal } from './marketing/screens-company';
import { ScreenDwindling } from './marketing/screens-dwindling';
import { ScreenFeaturePage } from './marketing/screens-feature-pages';
import { ScreenFeatures } from './marketing/screens-features';
import { ScreenFurrow } from './marketing/screens-furrow';
import { ScreenHome } from './marketing/screens-home';
import { ScreenMewsli } from './marketing/screens-mewsli';
import { ScreenNinelives } from './marketing/screens-ninelives';
import { ScreenPricing } from './marketing/screens-pricing';
import { ScreenSunder } from './marketing/screens-sunder';
import { applyPageHead } from './seo/pageMeta';
import { setAuthExpiredHandler } from './services/api';
import { ScreenShowcase } from './showcase/screens-showcase';

export function ScreenNotFound() {
  return (
    <NotFoundScreen
      className="ss-screen"
      style={{ height: '100vh', gap: 24, padding: undefined }}
      glyphSize={48}
      title="404"
      titleStyle={{ fontSize: 52 }}
      body="This world isn't published. The link may be old, or the game was unlisted by its creator."
      bodyStyle={{ color: 'var(--ss-text-2)', maxWidth: 380, lineHeight: undefined }}
      ctas={[{ label: 'Back to Soulstack', to: '/' }]}
      ctaMarginTop={0}
    />
  );
}

/* Route param adapters for screens that take plain props. */
function FeaturePillarRoute() {
  const { pillar = 'editor' } = useParams();
  return <ScreenFeaturePage pillar={pillar} />;
}
function LegalRoute() {
  const { doc } = useParams();
  return <ScreenLegal doc={doc} />;
}

/**
 * The public routes, as a fragment of <Route> elements for a <Routes>. Every
 * path publicPaths() lists (src/studio/seo/publicRoutes.ts) resolves here.
 */
export function publicRoutes() {
  return (
    <>
      {/* Marketing */}
      <Route path="/" element={<ScreenHome />} />
      <Route path="/features" element={<ScreenFeatures />} />
      <Route path="/features/:pillar" element={<FeaturePillarRoute />} />
      <Route path="/pricing" element={<ScreenPricing />} />
      <Route path="/showcase" element={<ScreenShowcase />} />
      <Route path="/sunder" element={<ScreenSunder />} />
      <Route path="/ninelives" element={<ScreenNinelives />} />
      <Route path="/furrow" element={<ScreenFurrow />} />
      <Route path="/dwindling" element={<ScreenDwindling />} />
      <Route path="/mewsli" element={<ScreenMewsli />} />
      <Route path="/blog" element={<ScreenBlog />} />
      <Route path="/blog/post/:id" element={<ScreenBlogPost />} />
      <Route path="/changelog" element={<ScreenChangelog />} />
      <Route path="/about" element={<ScreenAbout />} />
      <Route path="/contact" element={<ScreenContact />} />
      <Route path="/legal" element={<LegalRoute />} />
      <Route path="/legal/:doc" element={<LegalRoute />} />
      <Route path="/attributions" element={<ScreenAttributions />} />

      {/* Docs: one route per content page */}
      <Route path="/docs" element={<ScreenDocsHome />} />
      {DOC_PAGES.filter((p) => p.path !== '/docs').map((p) => (
        <Route key={p.path} path={p.path} element={<ScreenDocsPage page={p} />} />
      ))}
    </>
  );
}

/**
 * Scroll container matching the design template's site-scroll behaviour. It
 * also keeps the document head (title, description, canonical, Open Graph)
 * in step with the route after client-side navigation; the pre-rendered HTML
 * already carries the same values for the first load.
 */
export function SiteScroll({ children }: { children: ReactNode }) {
  const route = useRoute();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
    applyPageHead(route);
  }, [route]);
  return (
    <div className="site-scroll" ref={ref} data-screen-label={route}>
      {children}
    </div>
  );
}

/**
 * Route to sign-in whenever the API reports the session has expired (401), so a
 * stale token surfaces as a redirect rather than a raw error deep in the UI.
 */
function AuthExpiryBridge() {
  useEffect(() => {
    setAuthExpiredHandler(() => navigate('/signin'));
    return () => setAuthExpiredHandler(null);
  }, []);
  return null;
}

/**
 * The providers and bridges around every route. `router` is the router
 * element to render inside them (BrowserRouter in the browser, StaticRouter
 * in the pre-render), whose children are the routes.
 */
export function SiteFrame({ router: Router, children }: { router: ComponentType<{ children?: ReactNode }>; children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <NavigateBridge />
            <AuthExpiryBridge />
            <SiteScroll>{children}</SiteScroll>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
